import * as React from "react";

import { ApiError, renameHandle } from "@/api/client";
import { HANDLE_REGEX, ProfileView } from "@/api/types";

/**
 * One-time rename prompt for handles that predate the letters+digits rule
 * (i.e. contain a hyphen). Shown only while
 * {@code profile.handleNeedsMigration} — after a successful rename the
 * backend reports false and the panel disappears for good.
 */
export function HandleMigrationPanel({
  currentHandle,
  onRenamed,
}: {
  currentHandle: string;
  onRenamed: (profile: ProfileView) => void;
}) {
  const [handle, setHandle] = React.useState(currentHandle.replace(/-/g, ""));
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const normalised = handle.trim().toLowerCase().replace(/^@/, "");
  const formatOk = HANDLE_REGEX.test(normalised);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting || !formatOk) return;
    setError(null);
    setSubmitting(true);
    try {
      onRenamed(await renameHandle(normalised));
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError("That handle is taken. Try another.");
      } else if (err instanceof ApiError && err.status === 400) {
        setError("Handles are 3–30 lowercase letters and digits, nothing else.");
      } else {
        setError(err instanceof Error ? err.message : "Couldn't rename your handle.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="comic-shadow rounded-xl border-2 border-warning/40 bg-warning/10 p-5 text-warning-strong">
      <h2 className="font-comic-headline text-lg uppercase tracking-wide">
        Choose your card handle
      </h2>
      <p className="mt-1 text-sm">
        Handles are now letters and digits only, and yours (
        <code>@{currentHandle}</code>) has a hyphen. Pick its replacement:{" "}
        <strong>this is a one-time change</strong> and it becomes your agent&apos;s
        public address.
      </p>
      <form className="mt-3 flex items-start gap-2" onSubmit={onSubmit}>
        <div className="flex-1">
          <div className="flex items-center gap-1 rounded-md border-2 border-border bg-background px-3 py-2">
            <span className="text-sm text-muted-foreground">@</span>
            <input
              type="text"
              value={handle}
              onChange={(e) => setHandle(e.target.value)}
              disabled={submitting}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full bg-transparent text-sm text-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>
          <p className="mt-1 text-[11px]">
            3–30 lowercase letters and digits. No other characters.
          </p>
          {error && (
            <p role="alert" className="mt-1 text-[11px] font-semibold text-destructive-strong">
              {error}
            </p>
          )}
        </div>
        <button
          type="submit"
          disabled={submitting || !formatOk}
          className="comic-shadow-sm h-[40px] shrink-0 rounded-md border-2 border-border bg-brand-blue px-4 font-comic-headline text-sm uppercase tracking-wide text-brand-blue-foreground hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Renaming…" : "Rename"}
        </button>
      </form>
    </div>
  );
}
